import './styles.scss';

import { Button } from '@ets-global/b2c-website-ui';
import classNames from 'classnames';
import { fromJS } from 'immutable';
import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import SessionSearchBanner from '../../../components/Base/Banner/SessionSearchBanner';
import NotFoundHandler from '../../../components/NotFoundHandler';
import { LocalizedRedirect } from '../../../components/Routing';
import SkeletonItem from '../../../components/SkeletonItem';
import { COOKIE_MAX_AGE } from '../../../constants';
import { CANCELLED_PURCHASE, CONFIRMED_PURCHASE } from '../../../graphql/queries/purchase';
import { getParams } from '../../../helpers/router';
import { useAuth, useDataLayer, useQuery, useRouter } from '../../../hooks';
import { paths } from '../../../paths';

const PurchaseFinalisation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    generatePath,
    currentRoute: { params },
  } = useRouter();
  const [cookies, setCookie] = useCookies(['purchaseFunnelRegistrationId']);
  const { registrationId, type } = params;
  const { status } = getParams(location.search);
  const { isAuthenticated } = useAuth();
  const { pushEventLoadedPurchaseFunnel, pushEventLoadedCartPage } = useDataLayer();

  const redirectToFail = Boolean(status && status !== 'OK' && type === 'success');
  const {
    loading: loadingConfirmedPurchase,
    data: { confirmedPurchase },
  } = useQuery(CONFIRMED_PURCHASE, {
    variables: { registrationId },
    skip: redirectToFail || type === 'fail',
  });

  useEffect(() => {
    if (confirmedPurchase && cookies?.purchaseFunnelRegistrationId !== registrationId) {
      pushEventLoadedPurchaseFunnel({
        type: 'common',
        stepName: 'confirmation',
        stepNumber: 4,
      });
      pushEventLoadedCartPage({
        eventCommand: 'eec_purchase',
        purchase: fromJS(confirmedPurchase),
      });

      setCookie('purchaseFunnelRegistrationId', registrationId, {
        path: location.pathname,
        maxAge: COOKIE_MAX_AGE,
      });
    }
  }, [confirmedPurchase, registrationId, setCookie, cookies]);

  const {
    loading: loadingCancelledPurchase,
    data: { cancelledPurchase },
  } = useQuery(CANCELLED_PURCHASE, {
    variables: { registrationId },
    skip: redirectToFail || type === 'success',
  });

  if (redirectToFail) {
    return (
      <LocalizedRedirect
        to={generatePath(paths.PURCHASE_PAYMENT_FINALISATION, {
          ...params,
          registrationId,
          type: 'fail',
        })}
      />
    );
  }

  let purchaseNumber = null;
  if (confirmedPurchase && confirmedPurchase.id) {
    purchaseNumber = confirmedPurchase.id;
  } else if (cancelledPurchase && cancelledPurchase.id) {
    purchaseNumber = cancelledPurchase.id;
  }

  if (!loadingConfirmedPurchase && !loadingCancelledPurchase && !purchaseNumber) {
    return <NotFoundHandler />;
  }

  return (
    <main className="main main--no-offset-top">
      <SessionSearchBanner />
      <div className={classNames('checkout-confirmation container container--full container--blue')}>
        {loadingConfirmedPurchase || loadingCancelledPurchase ? (
          <div className="container container--padding">
            <SkeletonItem className="small-title" style={{ height: '30px', width: '25%' }} />
            <div className="checkout-confirmation__block">
              <p>
                <SkeletonItem style={{ height: '13px', width: '100%' }} />
              </p>
              <p>
                <SkeletonItem style={{ height: '13px', width: '20%' }} />
              </p>
              <Button className={'skeleton-item'}>
                <div style={{ width: '10px', height: '16px' }} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="container container--padding">
            <h3 className="small-title">
              <Trans values={{ purchaseNumber }}>purchase-finalisation.purchase.{type}.title</Trans>
            </h3>
            <div className="checkout-confirmation__block">
              <p>
                <Trans values={{ purchaseNumber }}>purchase-finalisation.purchase.{type}.content</Trans>
              </p>
              {type !== 'success' && (
                <Button to={paths.SESSION_SEARCH} aria-label={t('session-registration.find-another-session')}>
                  <Trans>session-registration.find-another-session</Trans>
                </Button>
              )}
              {type === 'success' && !isAuthenticated && (
                <Button
                  to={paths.LOGIN}
                  search={`?registrationId=${registrationId}`}
                  data-cypress="login-purchase-link"
                  aria-label={t('purchase-finalisation.link.login')}
                >
                  <Trans>purchase-finalisation.link.login</Trans>
                </Button>
              )}
              {type === 'success' && isAuthenticated && (
                <Button
                  to={paths.ACCOUNT_PURCHASES}
                  data-cypress="account-purchase-link"
                  aria-label={t('purchase-finalisation.link.account-purchase')}
                >
                  <Trans>purchase-finalisation.link.account-purchase</Trans>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default PurchaseFinalisation;
